import {
  fetchCandidateMoveLogs as fetchCandidateMoveLogs$,
  fetchMoveLog as fetchMoveLog$,
  fetchMoveLogInsightBanner as fetchMoveLogInsightBanner$,
  fetchMoveLogInsightData as fetchMoveLogInsightData$,
  fetchMoveLogsData as fetchMoveLogsData$,
  fetchMoveLogsDataCount as fetchMoveLogsDataCount$,
  fetchPoiVisitCountRecent as fetchPoiVisitCountRecent$,
  fetchSuggestedMoveLogPosition as fetchSuggestedMoveLogPosition$,
  MoveLogCandidatesResBody,
  MoveLogCheckInReqParams,
  MoveLogCheckInResBody,
  MoveLogFetchResBody,
  MoveLogInsightBannerFetchResBody,
  MoveLogInsightDataFetchResBody,
  MoveLogModificationResBody,
  MoveLogModifyReqParams,
  MoveLogsDataFetchReqParams,
  MoveLogsFetchResBody,
  MoveLogsTotalCountFetchResBody,
  MoveLogSuggestedPositionFetchResBody,
  PoiVisitCountRecentFetchResBody,
  submitAddCheckInMoveLog as submitAddCheckInMoveLog$,
  submitMoveLogDeletion as submitMoveLogDeletion$,
  submitMoveLogModification as submitMoveLogModification$,
} from '@tmap-web-lib/remote-api-client/frontman'
import { getRandomNumber } from '@tmap-web-lib/utils'
import { AxiosResponse } from 'axios'
import dayjs from 'dayjs'
import { getDefaultStore } from 'jotai'
import { v4 } from 'uuid'

import { mockHost } from '@/apis'
import { storageMockEnabled } from '@/atoms'
import { GeoCoordinates } from '@/features/web/move_log/shared-types'
import { isBrowser } from '@/utils'

const MockData = import('@/features/web/move_log/mock')
const MOCK = isBrowser() ? new URLSearchParams(location.search).get('mock') === 'true' : false
const SQUARE_ADMIN_MOCK = getDefaultStore().get(storageMockEnabled)

type MoveLogItemData = MoveLogsFetchResBody['logs'][number]

export async function fetchMoveLogsDataCount(params: MoveLogsDataFetchReqParams) {
  if (!MOCK) {
    return fetchMoveLogsDataCount$(params)
  }

  await new Promise<void>((resolve) => setTimeout(resolve, 333))
  const { data } = await fetchMoveLogsData(params)
  return Promise.resolve({
    data: {
      status: 'NORMAL',
      logCount: data.logCount,
    },
  } as AxiosResponse<MoveLogsTotalCountFetchResBody>)
}

export async function fetchMoveLogsData(params: MoveLogsDataFetchReqParams) {
  if (!MOCK && !SQUARE_ADMIN_MOCK) {
    return fetchMoveLogsData$(params) as unknown as AxiosResponse<MoveLogsFetchResBody>
  }

  if(SQUARE_ADMIN_MOCK) {
    return mockHost.get<MoveLogsFetchResBody>('/api/v1/timeline',{ params })
  }

  const {
    from,
    to,
    sort = 'desc',
  } = params

  await new Promise<void>((resolve) => setTimeout(resolve, 333))
  const { MockMoveLogPagesData } = await MockData
  const fromDate = dayjs(from).startOf('d')
  const toDate = dayjs(to).startOf('d')
  const diff = toDate.diff(fromDate, 'day')
  let logs = []
  for (let i = 0; i <= diff; i++) {
    const d = sort === 'asc'
      ? fromDate.add(i, 'day').format('YYYY-MM-DD')
      : toDate.subtract(i, 'day').format('YYYY-MM-DD')
    logs.push(...((sort === 'asc' ? MockMoveLogPagesData[d] : MockMoveLogPagesData[d]?.toReversed()) ?? []))
  }

  logs = logs.filter(item => item.logType !== 'CANDIDATE')

  return Promise.resolve({
    data: {
      status: 'NORMAL',
      logs,
      logCount: logs.length,
    },
  } as AxiosResponse<MoveLogsFetchResBody>)
}

export async function submitMoveLogDeletion({ logId }: { logId: string }) {
  if (!MOCK) {
    return submitMoveLogDeletion$({ logId })
  }

  await new Promise<void>((resolve) => setTimeout(resolve, 333))
  const { MockMoveLogPagesData, MockMoveLogPagesDataInserted, MockMoveLogsData } = await MockData
  const data = MockMoveLogPagesDataInserted.get(logId) ?? MockMoveLogsData.find(item => item.logId === logId)!
  const list = MockMoveLogPagesData[dayjs(data.visitedAt).format('YYYY-MM-DD')]
  const index = list.findIndex(item => item.logId === logId)
  list.splice(index, 1)
  MockMoveLogPagesDataInserted.delete(logId)
}

export async function submitMoveLogModification({ logId, ...payload }: MoveLogModifyReqParams) {
  if (!MOCK) {
    return submitMoveLogModification$({ logId, ...payload })
  }

  await new Promise<void>((resolve) => setTimeout(resolve, 333))
  const { MockMoveLogPagesData, MockMoveLogPagesDataInserted, MockMoveLogsData } = await MockData
  const date = dayjs(payload.visitedAt).format('YYYY-MM-DD')
  const source = MockMoveLogPagesDataInserted.get(logId) ?? MockMoveLogsData.find(item => item.logId === logId)!
  const modified = {
    ...source,
    ...payload,
    logId,
    place: {
      ...source.place,
      ...payload.place,
      name: payload.place.poiName,
    },
  }
  if (!MockMoveLogPagesData[date]) {
    MockMoveLogPagesData[date] = []
    MockMoveLogPagesData[date].push(modified as any)
  }
  return Promise.resolve({
    data: { log: modified },
  } as unknown as AxiosResponse<MoveLogModificationResBody>)
}

export async function submitAddCheckInMoveLog({ poiName, ...payload }: MoveLogCheckInReqParams) {
  if (!MOCK) {
    return submitAddCheckInMoveLog$({ poiName, ...payload })
  }

  await new Promise((resolve) => setTimeout(resolve, 333))
  const { MockMoveLogPagesData, MockMoveLogPagesDataInserted, MockMoveLogsData } = await MockData
  const logId = v4()
  const base = MockMoveLogsData.find(item => item.logId === payload.candidateLogId)
    ?? MockMoveLogsData[getRandomNumber() % MockMoveLogsData.length]
  const newData = {
    ...base,
    place: {
      ...base.place,
      poiId: payload.poiId,
      name: poiName,
    },
    visitedAt: payload.visitedAt,
    transportMode: payload.transportMode,
    logType: 'WALK_IN',
    logId: logId,
    visitOrder: base.visitCount + 1,
  } satisfies MoveLogItemData
  MockMoveLogPagesDataInserted.set(logId, newData)

  const date = dayjs(payload.visitedAt).format('YYYY-MM-DD')
  MockMoveLogPagesData[date] ??= []
  MockMoveLogPagesData[date].push(newData)

  return Promise.resolve({
    data: {
      pointResult: base.checkinPoint ? {
        result: 'GRANTED',
        amount: base.checkinPoint,
      } : null,
      log: newData,
      availablePointCount: newData.reviewModule?.expectedPoint ?? 0,
    },
  } as unknown as AxiosResponse<MoveLogCheckInResBody>)
}

export async function fetchMoveLog({ logId, from, to, isBookmarked }: { logId: string; from: string; to: string; isBookmarked: boolean }) {
  if (!MOCK) {
    return fetchMoveLog$({ logId, from, to })
  }

  await new Promise<void>((resolve) => setTimeout(resolve, 333))
  const { MockMoveLogPagesDataInserted, MockMoveLogsData } = await MockData
  const source = (MockMoveLogPagesDataInserted.get(logId) ?? MockMoveLogsData.find(item => item.logId === logId))!
  return Promise.resolve({
    data: {
      ...MockMoveLogPagesDataInserted.get(logId) ?? MockMoveLogsData.find(item => item.logId === logId),
      isBookmarked,
      review: {
        reviewId: '',
        rating: getRandomNumber() % 5 + 1,
        text: source.review?.text ?? '',
        imageCount: source.review?.imageCount ?? 0,
        representativeImageUrl: source.review?.representativeImageUrl ?? '',
        isContinuation: false,
      },
    },
  } as unknown as AxiosResponse<MoveLogFetchResBody>)
}

export async function fetchSuggestedMoveLogPosition() {
  if (!MOCK) {
    return fetchSuggestedMoveLogPosition$()
  }

  await new Promise((resolve) => setTimeout(resolve, 333))
  return Promise.resolve({
    data: {
      visitedAt: '2026-03-23T10:20:30',
      latitude: 37.56271603684371,
      longitude: 126.992092045089,
    },
  } as AxiosResponse<MoveLogSuggestedPositionFetchResBody>)
}

export async function fetchCandidateMoveLogs({ longitude, latitude }: GeoCoordinates) {
  if (!MOCK) {
    return fetchCandidateMoveLogs$({
      longitude,
      latitude,
    })
  }

  await new Promise((resolve) => setTimeout(resolve, 333))
  const { MockMoveLogsData } = await MockData
  const start = Math.max(0, getRandomNumber() % MockMoveLogsData.length - 3)
  const end = Math.min(MockMoveLogsData.length - 1, start + 5)

  const candidates: MoveLogCandidatesResBody['candidates'] = MockMoveLogsData.slice(start, end).map(item => ({
    logId: v4(),
    checkinModule: item.reviewModule ? { checkinPoint: item.reviewModule.expectedPoint } : null,
    imageUrl: item.review?.representativeImageUrl ?? null,
    logType: 'CANDIDATE',
    poiId: item.place.poiId,
    poiName: item.place.name,
    visitedAt: item.visitedAt,
  }))

  return Promise.resolve({
    data: {
      candidateCount: candidates.length,
      candidates,
    },
  } as AxiosResponse<MoveLogCandidatesResBody>)
}

export async function fetchPoiVisitCountRecent() {
  if (!MOCK) {
    return fetchPoiVisitCountRecent$()
  }
  return Promise.resolve({
    data: {
      visitedCount: getRandomNumber() % 20,
    },
  } as AxiosResponse<PoiVisitCountRecentFetchResBody>)
}

export async function fetchMoveLogInsightBanner({ excludeHomeCompany }: { excludeHomeCompany?: boolean } = {}) {
  if (!MOCK) {
    return fetchMoveLogInsightBanner$({ excludeHomeCompany })
  }

  await new Promise((resolve) => setTimeout(resolve, 333))

  const { MockMoveLogsData } = await MockData
  const min = Math.max(0, (getRandomNumber() % MockMoveLogsData.length) - 30)
  const max = Math.min(MockMoveLogsData.length - 1, min + getRandomNumber() % MockMoveLogsData.length)
  const data = MockMoveLogsData.slice(min, max)
  return Promise.resolve({
    data: {
      topPlace: {
        poiName: '',
        visitCount: getRandomNumber() % 100,
      },
      discoveredPlace: {
        discoveredCount: getRandomNumber() % 100,
      },
      category: {
        firstCategoryName: data[0]?.place.poiCategory.level1.name,
        secondCategoryName: data[0]?.place.poiCategory.level2.name,
      },
      region: {
        firstRegion: '',
        secondRegion: '',
        thirdRegion: '',
      },
    },
  } as AxiosResponse<MoveLogInsightBannerFetchResBody>)
}

export async function fetchMoveLogInsightData({ excludeHomeCompany }: { excludeHomeCompany?: boolean } = {}) {
  if (!MOCK) {
    return fetchMoveLogInsightData$({ excludeHomeCompany })
  }

  await new Promise((resolve) => setTimeout(resolve, 333))
  const { MockMoveLogsData } = await MockData
  const data: MoveLogInsightDataFetchResBody = {
    summary: (() => {
      const Mock = MockMoveLogsData.filter(item => item.place.imageUrl).map(item => item.place.imageUrl)
      const start = Math.max(0, getRandomNumber() % Mock.length - 5)
      const imageList = Mock.slice(start, start + (getRandomNumber() % 5))
      return {
        periodDays: 30,
        periodFrom: dayjs().subtract(28, 'd').format('YYYY-MM-DD'),
        periodTo: dayjs().format('YYYY-MM-DD'),
        totalPlaceCount: 30,
        avgDailyPlaceCount: 30,
        activeDayCount: 30,
        activeDayDiff: 30,
        reviewImages: imageList,
      }
    })(),
    topPlaces: {
      places: (() => {
        const start = Math.max(0, getRandomNumber() % MockMoveLogsData.length - 3)
        return MockMoveLogsData.slice(start, start + 3).map((item, index) => ({
          rank: index + 1,
          poiId: item.place.poiId,
          poiName: item.place.name,
          imageUrl: item.place.imageUrl,
          poiCategoryName: item.place.poiCategory.level2.name,
          visitCount: getRandomNumber() % 99,
          rankChange: (['NEW', 'UP', 'DOWN', 'SAME'] as const)[getRandomNumber() % 4],
        }))
      })(),
    },
    discoveredPlaces: (() => {
      const start = Math.max(0, getRandomNumber() % MockMoveLogsData.length - (getRandomNumber() % MockMoveLogsData.length))
      const data = MockMoveLogsData.slice(
        start,
        Math.min(MockMoveLogsData.length, start + (getRandomNumber() % (MockMoveLogsData.length - start))),
      )
      return {
        discoveredCount: data.length,
        places: data.map(item => ({
          poiId: item.place.poiId,
          poiName: item.place.name,
          imageUrl: item.place.imageUrl,
        })),
      }
    })(),
    categories: {
      totalPlaceCount: 130,
      items: [
        { rank: 1, poiCategoryName: '음식점', poiCategoryCode: '', placeCount: 52, ratio: 0 },
        { rank: 2, poiCategoryName: '카페', poiCategoryCode: '', placeCount: 16, ratio: 0 },
        { rank: 3, poiCategoryName: '레저 ∙ 스포츠', poiCategoryCode: '', placeCount: 11, ratio: 0 },
        { rank: 4, poiCategoryName: '관광명소', poiCategoryCode: '', placeCount: 8, ratio: 0 },
        { rank: 5, poiCategoryName: '슈퍼 ∙ 마트', poiCategoryCode: '', placeCount: 5, ratio: 0 },
        { rank: 6, poiCategoryName: '복합쇼핑몰', poiCategoryCode: '', placeCount: 4, ratio: 0 },
        { rank: 7, poiCategoryName: '문화생활시설', poiCategoryCode: '', placeCount: 4, ratio: 0 },
        { rank: 8, poiCategoryName: '그 외', poiCategoryCode: '', placeCount: 4, ratio: 0 },
      ],
    },
    regions: {
      items: [
        { rank: 1, region: '부산 수영구', visitCount: 220 },
        { rank: 2, region: '대전 유성구', visitCount: 5 },
        { rank: 3, region: '서울 성북구', visitCount: 2 },
      ],
    },
    transportModeStats: {
      walkCount: 30,
      driveCount: 70,
      walkRatio: 0.3,
      driveRatio: 0.7,      
    }
  }

  return Promise.resolve({
    data,
  })
}

export interface ErrorResBody<Code = string, Detail = Record<string, any>> {
  code: Code
  detail: Detail
  message: string
  timestamp: string
}
